import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Facebook,
  LinkedIn,
  WhatsApp,
  Telegram,
  Email,
  ArrowBack,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import SEO from '../components/SEO';
import XLogo from '../components/XLogo';

const CREDICONNECT = '/portfilio/Branding%20-%20Crediconnect';
const FOTE = '/portfilio/Events%20Coverage%20-%20FOTE';
const ILO = '/portfilio/Project%20Documentation%20%E2%80%93%20ILO';

const projectData: Record<string, {
  title: string;
  overview: string | string[];
  servicesProvided: string;
  description: string | string[];
  skillsNeeded: string;
  detailImages?: string[];
}> = {
  'sustain-documentary-series': {
    title: 'Sustain Documentary Series',
    overview: [
      'Sustain is a documentary series highlighting sustainable initiatives, featuring inspiring stories from organic farming to cutting-edge green technologies. The show celebrates the resilience of individuals and communities championing eco-friendly practices, sparking conversations and promoting a brighter, more sustainable future.',
      'Sustain provides NGOs, government agencies, and companies involved in development work with a platform to publish their work or to educate public audiences about issues pertaining to their work. The show has been broadcast on 3Ktv since 2022.',
      'Fans of Africa is a documentary-style series that explores the real stories, emotions, and cultures of everyday sports fans across the continent. The show captures authentic moments of passion, rivalry, and community, revealing how sport connects people and reflects daily life. It embodies Bizani’s commitment to documenting real life by highlighting the humanity behind the fan. The show was broadcast on 3Ktv.',
      'Ndine Thaza is dedicated to empowering Zimbabwe’s youth by showcasing stories of entrepreneurship, innovation, and resilience. Through inspiring real-life journeys, the show connects brands with an engaged audience. The show started broadcasting on ZBCtv in 2018 and has grown to become one of Zimbabwe’s most loved and known local reality TV shows.'
    ],
    servicesProvided: 'Documentary Series Production.',
    description: [
      'Sustain — 3Ktv since 2022. Fans of Africa — 3Ktv. Ndine Thaza — ZBCtv since 2018.'
    ],
    skillsNeeded: 'Documentary',
    detailImages: [
      '/portfilio/sustain-documentary-series/video-production-scaled.jpg',
      '/portfilio/sustain-documentary-series/Biogas-digester-construction-1-scaled.jpg',
      '/portfilio/sustain-documentary-series/%C2%A9-2021.-%E2%80%A2-Ndine-Thaza-season-6-covid-19-special.jpg',
      '/portfilio/sustain-documentary-series/%C2%A9-2024.-%E2%80%A2-Zimbabwe-Sunshine-Group-buys-waste-from-Mabvuku.jpg'
    ]
  },
  'branding-crediconnect': {
    title: 'Branding – Crediconnect',
    overview: 'Branding is a process of storytelling, and office or operational spaces provide a great opportunity to tell a story that resonates with your visitors or clients. Bizani provides premise branding for Crediconnect’s nationwide branch network.',
    servicesProvided: 'Banking halls and corporate offices branding. Locations: Mutare CBD (x2), Newlands, Avondale, Harare CBD (x2), Gweru, Kadoma, Marondera, Bindura, Mutoko, Kwekwe, Chinhoyi.',
    description: 'Our work includes conceptualisation, design, print, installation, and maintenance of vinyl prints, wall murals, light boxes, and directional signage for 13 branches around Zimbabwe.',
    skillsNeeded: 'Branding',
    detailImages: [
      `${CREDICONNECT}/Branding-Crediconnect.jpg`,
      `${CREDICONNECT}/Branding-Crediconnect-1.jpg`,
      `${CREDICONNECT}/Branding-Crediconnect-2.jpg`,
      `${CREDICONNECT}/Branding-Crediconnect-3.jpg`
    ]
  },
  'events-coverage-fote': {
    title: 'Events Coverage – FOTE (Voices for Trees)',
    overview: [
      'The Forestry Commission of Zimbabwe leads national awareness campaigns on key forestry commemorations and public service issues. Since 2023, Bizani Media has supported these efforts by creating engaging digital content, including social media countdown graphics and short videos for National Tree Planting Day, as well as a 10-second, six-day countdown broadcast on ZBC TV before the evening news.',
      'We have also amplified forestry messaging through our Sustain TV show on 3Ktv, providing meaningful publicity for Forestry Commission programmes nationwide.',
      'For the 2025 National Tree Planting Day, Bizani produced the Voices for Trees series — a collection of mobile-friendly videos featuring forestry stakeholders across Zimbabwe. Contributions included both professionally filmed content and self-recorded clips, enhanced with subtitles and motion graphics. The campaign achieved strong organic reach, with the videos widely shared across partner networks and community platforms.'
    ],
    servicesProvided: 'Documentaries, Strategy, Publicity, Photography, Social Media',
    description: [
      'As part of their education and awareness, the Friends of the Environment Trust (FOTE) walks for approximately 150 km every year, planting trees and setting up tree nurseries, and raising awareness in communities. Bizani has covered the event, producing a documentary each year since 2015 (Annual Tree Planting Walkathon — Plumtree, Zvishavane, Chinhoyi, Mhondoro, Guruve, Bikita, Masvingo, Chipinge, Murombedzi).',
      'Photoshoot and framing for Board of Directors.',
      'The Voices for Trees campaign collected 30 video clips from across Zimbabwe and compiled viral, mobile-friendly short clips as countdown to National Tree Planting Day.'
    ],
    skillsNeeded: 'Documentary',
    detailImages: [
      `${FOTE}/Events-Coverage-FOTE-1.jpg`,
      `${FOTE}/Events-Coverage-FOTE-1-i.jpg`
    ]
  },
  'project-documentation-ilo': {
    title: 'Project Documentation – ILO (Spotlight Initiative)',
    overview: [
      'The International Labour Organisation (ILO) was part of the Spotlight Initiative, a multi United Nations agency project, and implemented the women’s economic empowerment program in Chipinge, Hopley, and Epworth over a period of 3 years.',
      'Bizani compiled the final project report, which included interviewing beneficiaries, photography, design, typesetting, and print of a 40-page publication.'
    ],
    servicesProvided: 'Documentary, Publication, Printing, Photography',
    description: 'Our work involved interviewing beneficiaries, photography, design, typesetting, and printing a 40-page publication.',
    skillsNeeded: 'Documentary',
    detailImages: [
      `${ILO}/Perpetua-Komoni-farmer.-Chisumbanje-Chipinge.-ZIMBABWE.-Sept-2023.-%C2%A9Shaun-Chitsiga-ILO-2-scaled.jpg`,
      `${ILO}/Fortunate-Garawa-a-farmer-in-Vheneka-Chipinge-ZIMBABWE-using-drip-irrigation.-Sept-2023.-%C2%A9Shaun-Chitsiga-ILO-3-scaled.jpg`,
      `${ILO}/Martha-Simango-fashion-designer.-Machona-Chipinge.-ZIMBABWE.-Sept-2023.-%C2%A9Shaun-Chitsiga-ILO-4-scaled.jpg`
    ]
  },
  'human-rights-ngo-forum': {
    title: 'Human Rights NGO Forum',
    overview: 'The Zimbabwe Human Rights NGO Forum uses film to document and memorialise stories of survivors and victims of human rights atrocities. Bizani has produced two one-hour documentaries covering stories about recent and past atrocities in various parts of Zimbabwe.',
    servicesProvided: 'Documentary. Locations: Zaka, Silobela, Lupane, Chegutu, Tsholotsho, Bhalagwe, Bulawayo.',
    description: [
      'Unforgotten Echoes (2023).',
      'Justice Delayed Is Justice Denied (2021).'
    ],
    skillsNeeded: 'Documentary',
    detailImages: ['/extraimages/Human%20Rights%20NGO%20Forum.jpg.jpeg']
  },
  'fote-walkathon': {
    title: 'Annual Tree Planting Walkathon – FOTE',
    overview: 'As part of their education and awareness, the Friends of the Environment Trust (FOTE) walks for approximately 150 km every year, planting trees and setting up tree nurseries, and raising awareness in communities. Bizani has covered the event, producing a documentary each year since 2015.',
    servicesProvided: 'Documentaries, Social Media. Locations: Plumtree, Zvishavane, Chinhoyi, Mhondoro, Guruve, Bikita, Masvingo, Chipinge, Murombedzi.',
    description: 'Annual Tree Planting Walkathon event coverage — documentary production each year since 2015.',
    skillsNeeded: 'Documentary',
    detailImages: ['/extraimages/Annual%20Tree%20Planting%20Walkathon%20%E2%80%93%20FOTE.JPG.jpeg']
  },
  'ilo-sedway': {
    title: 'ILO SEDWAY Project',
    overview: 'With funding from the African Development Bank and in partnership with the Government of Zimbabwe, the International Labour Organisation is implementing the Sustainable Enterprise Development for Women and Youth (SEDWAY) project in six districts across Zimbabwe. Bizani is tracking the project by visiting the project districts at various stages and documenting progress through photography, beneficiary interviews, documentaries, social media cards, web stories, key events coverage, and publications.',
    servicesProvided: 'Documentary, Publication, Social Media & Digital, Photography. Locations: Bulawayo, Mutare, Masvingo, Gweru, Mutoko, Haura, Karoi.',
    description: 'Documentation of the SEDWAY project across six districts — photography, beneficiary interviews, documentaries, social media, web stories, and event coverage.',
    skillsNeeded: 'Documentary',
    detailImages: ['/extraimages/ILO%20SEDWAY%20project.JPG.jpeg']
  },
  'environment-africa-sustainable-energy': {
    title: 'Upscaling Sustainable Energy – Environment Africa',
    overview: 'Environment Africa implemented the Upscaling Sustainable Energy for Biodiversity Conservation project with funding from UNDP GEF-6. The project promoted biogas energy for cooking, solar energy for lighting, wood-saving stoves, energy woodlots for firewood, and community-based financing schemes for purchasing energy products on lay-by. Bizani produced a 25-minute television documentary (aired on 3Ktv), a short online video, and photographs for various storytelling functions including project reports and social media.',
    servicesProvided: 'Documentary, Photography. Locations: Mbire, Muzarabani, Hurungwe.',
    description: '25-minute TV documentary (3Ktv), short online video, and photography for project reports and social media.',
    skillsNeeded: 'Documentary',
    detailImages: ['/extraimages/Upscaling%20Sustainable%20Energy%20%E2%80%93%20Environment%20Africa.JPG.jpeg']
  }
};

const CAROUSEL_INTERVAL_MS = 5000;

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const project = slug ? projectData[slug] : null;
  const images = project?.detailImages ?? [];
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [images.length]);

  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  const goNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);

  const BASE_URL = 'https://www.bizani.co.zw';
  const projectJsonLd = useMemo<object[]>(() => {
    if (!project || !slug) return [];
    const overviewText = Array.isArray(project.overview) ? project.overview.join(' ') : project.overview;
    const desc = overviewText.replace(/\s+/g, ' ').trim().slice(0, 160);
    const projectUrl = `${BASE_URL}/work/${slug}`;
    const img = images.length > 0 ? images[0] : undefined;
    const projectImageUrl = img?.startsWith('http') ? img : img ? `${BASE_URL}${img}` : undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
          { '@type': 'ListItem', position: 2, name: 'Our Work', item: `${BASE_URL}/work` },
          { '@type': 'ListItem', position: 3, name: project.title, item: projectUrl }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CreativeWork',
        name: project.title,
        description: desc,
        url: projectUrl,
        ...(projectImageUrl && { image: projectImageUrl }),
        author: { '@type': 'Organization', name: 'Bizani Media', url: BASE_URL }
      }
    ];
  }, [project, slug, images]);

  if (!project) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-headline mb-4">Project not found</h1>
          <Link to="/work" className="text-bizani-orange hover:underline">
            Back to Our Work
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(project.title);
  const overviewText = Array.isArray(project.overview) ? project.overview.join(' ') : project.overview;
  const shareText = encodeURIComponent(overviewText);
  const seoDescription = overviewText.replace(/\s+/g, ' ').trim().slice(0, 160);
  const seoImage = images.length > 0 ? images[0] : undefined;

  const shareLinks = [
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, Icon: Facebook },
    { name: 'X', href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`, Icon: XLogo },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`, Icon: LinkedIn },
    { name: 'WhatsApp', href: `https://wa.me/?text=${shareTitle}%20${shareUrl}`, Icon: WhatsApp },
    { name: 'Telegram', href: `https://t.me/share/url?url=${shareUrl}&text=${shareTitle}`, Icon: Telegram },
    { name: 'Email', href: `mailto:?subject=${shareTitle}&body=${shareText}%20${shareUrl}`, Icon: Email }
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      <SEO
        title={project.title}
        description={seoDescription}
        image={seoImage}
        path={slug ? `/work/${slug}` : '/work'}
        keywords={`${project.title}, Bizani Media, ${project.skillsNeeded}, Zimbabwe, case study, portfolio`}
        jsonLd={projectJsonLd}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-bizani-orange transition-colors mb-8 font-body"
          >
            <ArrowBack style={{ fontSize: '20px' }} />
            Back to Our Work
          </Link>

          {images.length > 0 && (
            <div className="relative rounded-xl overflow-hidden mb-10 bg-gray-900">
              <div className="relative aspect-video max-h-[480px]">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, x: 60 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -60 }}
                    transition={{ duration: 0.4 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={images[currentIndex]}
                      alt={`${project.title} ${currentIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </AnimatePresence>
                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={goPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-10"
                      aria-label="Previous image"
                    >
                      <ChevronLeft style={{ fontSize: 28 }} />
                    </button>
                    <button
                      type="button"
                      onClick={goNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-10"
                      aria-label="Next image"
                    >
                      <ChevronRight style={{ fontSize: 28 }} />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setCurrentIndex(i)}
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i === currentIndex ? 'bg-bizani-orange scale-110' : 'bg-white/60 hover:bg-white/80'
                          }`}
                          aria-label={`Go to image ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-12" style={{ color: '#EF3F23' }}>
            Project Description
          </h1>

          <div className="space-y-10 font-body text-gray-300">
            <section>
              <h2 className="text-xl font-headline font-bold text-white mb-3">Overview</h2>
              {Array.isArray(project.overview) ? (
                <div className="space-y-3">
                  {project.overview.map((para, i) => (
                    <p key={i} className="leading-relaxed">{para}</p>
                  ))}
                </div>
              ) : (
                <p className="leading-relaxed">{project.overview}</p>
              )}
            </section>

            <section>
              <h2 className="text-xl font-headline font-bold text-white mb-3">Services Provided</h2>
              <p className="leading-relaxed">{project.servicesProvided}</p>
            </section>

            <section>
              <h2 className="text-xl font-headline font-bold text-white mb-3">Description</h2>
              {Array.isArray(project.description) ? (
                <div className="space-y-3">
                  {project.description.map((para, i) => (
                    <p key={i} className="leading-relaxed">{para}</p>
                  ))}
                </div>
              ) : (
                <p className="leading-relaxed">{project.description}</p>
              )}
            </section>

            <section>
              <h2 className="text-xl font-headline font-bold text-white mb-4">Project Details</h2>
              <p className="leading-relaxed">
                <span className="text-white font-semibold">Skills Needed:</span> {project.skillsNeeded}
              </p>
            </section>

            <section className="pt-8 border-t border-gray-700">
              <h2 className="text-xl font-headline font-bold text-white mb-4">
                Share This Story, Choose Your Platform!
              </h2>
              <div className="flex flex-wrap gap-4">
                {shareLinks.map(({ name, href, Icon }) => (
                  <a
                    key={name}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 text-gray-300 hover:bg-bizani-orange hover:text-white transition-all duration-200"
                    aria-label={`Share on ${name}`}
                    title={`Share on ${name}`}
                  >
                    {Icon === XLogo ? <Icon size={24} /> : <Icon style={{ fontSize: '24px' }} />}
                  </a>
                ))}
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;
